import * as Sentry from '@sentry/react';
import React from 'react';
import '@shopify/polaris/build/esm/styles.css';
import enTranslations from '@shopify/polaris/locales/en.json';
import { Frame, AppProvider, TopBar } from '@shopify/polaris';
import { useSafeLayoutEffect } from '@chakra-ui/react';
import { useState, useCallback, useEffect } from 'react';
import { Link as ReactRouterLink, Outlet, useLocation } from 'react-router-dom';
import { PageError } from './errorboundries/pageError';
import { userMenuActions, navigationMarkup } from './shell/navigation';
import { theme } from './shell/theme';
import { TabTitle } from './shell/helmet';
import { useToggle } from './shared/useToogle';
import { formatDate } from './shared/components/functions';
import HolidayModal from './shared/components/modals/holidayModal.jsx';
import Toasts from './shared/components/toasts/toasts';
import { analytics } from './analytics/segment';
import { inProduction } from './helpers/inProduction';
import { useAuthStore, useLookupStore, useApprovalSummaryStore } from './context/useStore';
import { useProfilePhoto } from './shell/useProfilePhoto';
import CommandPalette from './shell/commandPalette/CommandPalette.jsx';
import { useCommandPaletteStore, SHORTCUT_LABEL, HAS_KEYBOARD } from './shell/commandPalette/store';

function Link({ children, url = '', external, ...rest }) {
  // Use a regular a tag for external and download links
  if (isOutboundLink(url) || rest.download) {
    rest.target = '_blank';
    rest.rel = 'noopener noreferrer';
    return (
      <a href={url} {...rest} className="Polaris-Link">
        {children}
      </a>
    );
  }
  return (
    <ReactRouterLink to={url} {...rest}>
      {children}
    </ReactRouterLink>
  );
}

function isOutboundLink(url) {
  return /^(?:[a-z][a-z\d+.-]*:|\/\/)/.test(url);
}

const getSidebarToggleButtonStyle = (sidebarVisible) => ({
  position: 'fixed',
  top: '50%',
  left: sidebarVisible ? '224px' : '12px',
  width: '30px',
  height: '30px',
  zIndex: 520,
  border: '1px solid rgba(0, 0, 0, 0.08)',
  borderRadius: '999px',
  background: 'rgba(255, 255, 255, 0.92)',
  backdropFilter: 'blur(6px)',
  WebkitBackdropFilter: 'blur(6px)',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
  color: '#5c5f62',
  fontSize: '16px',
  fontWeight: 700,
  lineHeight: 1,
  display: 'grid',
  placeItems: 'center',
  transform: 'translateY(-50%)',
  cursor: 'pointer',
  transition: 'left 180ms ease, box-shadow 180ms ease'
});

const SIDEBAR_VISIBILITY_STORAGE_KEY = 'sidebar-visible';

const getInitialSidebarVisibility = () => {
  try {
    const storedValue = window.localStorage.getItem(SIDEBAR_VISIBILITY_STORAGE_KEY);
    if (storedValue === null) return true;
    return storedValue === 'true';
  } catch {
    return true;
  }
};

function PageWrapper() {
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const today = formatDate(new Date()).slice(5);
  const users = useLookupStore((state) => state.users);
  const pickingplaces = useLookupStore((state) => state.pickingplaces);
  const birthdays = Array.isArray(users) ? users?.filter((u) => !u.Inactief && u.BirthdayMonthDay === today) : [];

  //open secondary action links in navbar in the same tab
  const links = document.getElementsByClassName('Polaris-Navigation__SecondaryAction');
  for (let i = 0, linksLength = links.length; i < linksLength; i++) {
    if (links[i].hostname === window.location.hostname) {
      links[i].target = '_self';
      links[i].rel = 'noreferrer noopener';
    }
  }

  const [holidaymodalactivated, setHolidayModalActivated] = useState(false);
  const [shotcutmodalactivated, setShotcutmodalactivated] = useState(false);
  const [toastActive, setToastActive] = useState(false);
  const [toastErrorActive, setToastErrorActive] = useState(false);
  const openCommandPalette = useCommandPaletteStore((state) => state.open);
  const [userMenuActive, setUserMenuActive] = useToggle();
  const [mobileNavigationActive, setMobileNavigationActive] = useToggle();
  const [sidebarVisible, setSidebarVisible] = useState(getInitialSidebarVisibility);
  const [isDesktopViewport, setIsDesktopViewport] = useState(() => window.matchMedia('(min-width: 48em)').matches);
  const approvalSummary = useApprovalSummaryStore((state) => state.summary);
  const refreshApprovalSummary = useApprovalSummaryStore((state) => state.refresh);
  const profilePhoto = useProfilePhoto(user?.id);

  useSafeLayoutEffect(() => {
    document.documentElement.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 48em)');
    const onChange = (event) => setIsDesktopViewport(event.matches);
    setIsDesktopViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onChange);

    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_VISIBILITY_STORAGE_KEY, String(sidebarVisible));
    } catch {
      // Ignore write failures (e.g. private mode restrictions).
    }
  }, [sidebarVisible]);

  const handleLogoutAction = useCallback(() => logout(), [logout]);

  const handleNavigationToggle = useCallback(() => {
    const isDesktop = window.matchMedia('(min-width: 48em)').matches;

    if (isDesktop) {
      setSidebarVisible((previous) => !previous);
      return;
    }

    if (!sidebarVisible) setSidebarVisible(true);
    setMobileNavigationActive();
  }, [setMobileNavigationActive, sidebarVisible]);

  useEffect(() => {
    if (inProduction) analytics.page();
  }, [location]);

  // pending approval counts for the navigation badge: on navigation, every minute and when the tab regains focus
  useEffect(() => {
    if (!user?.id) return undefined;
    refreshApprovalSummary();
    const interval = setInterval(refreshApprovalSummary, 60000);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refreshApprovalSummary();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user?.id, location.pathname, refreshApprovalSummary]);

  const userMenuMarkup = user && (
    <TopBar.UserMenu
      actions={userMenuActions(handleLogoutAction, setHolidayModalActivated, holidaymodalactivated, setShotcutmodalactivated, shotcutmodalactivated)}
      name={user.username}
      avatar={profilePhoto || undefined}
      open={userMenuActive}
      onToggle={setUserMenuActive}
    />
  );

  // The top bar field is only the door to the Cmd+K palette (shell/commandPalette): focusing it opens the palette.
  const searchFieldMarkup = (
    <TopBar.SearchField value="" onChange={(value) => openCommandPalette(value)} onFocus={() => openCommandPalette('')} placeholder={`Search everything…${HAS_KEYBOARD ? ` ${SHORTCUT_LABEL}` : ''}`} />
  );

  const topBarMarkup = <TopBar showNavigationToggle={true} userMenu={userMenuMarkup} searchResultsVisible={false} searchField={searchFieldMarkup} onNavigationToggle={handleNavigationToggle} />;

  return (
    <AppProvider i18n={enTranslations} linkComponent={Link} theme={'light'}>
      <>
        <TabTitle />
        {/* <GlobalHotKeys keyMap={keyMap} handlers={handlers(setSearchFocused, navigate)} /> */}
        <Sentry.ErrorBoundary fallback={PageError} showDialog={false}>
          <Frame
            logo={theme(user).logo}
            topBar={topBarMarkup}
            navigation={sidebarVisible ? navigationMarkup(location, user, birthdays, pickingplaces, approvalSummary) : null}
            showMobileNavigation={mobileNavigationActive}
            onNavigationDismiss={setMobileNavigationActive}
          >
            {isDesktopViewport ? (
              <button
                type="button"
                onClick={handleNavigationToggle}
                style={getSidebarToggleButtonStyle(sidebarVisible)}
                title={sidebarVisible ? 'Collapse sidebar' : 'Expand sidebar'}
                aria-label={sidebarVisible ? 'Collapse sidebar' : 'Expand sidebar'}
              >
                {sidebarVisible ? '‹' : '›'}
              </button>
            ) : null}
            <Toasts toast={toastActive} setToast={setToastActive} errorToast={toastErrorActive} setErrorToast={setToastErrorActive} toastContent="Changes saved" />
            <div style={{ paddingBottom: '30px' }}>
              <Outlet />
            </div>
            <HolidayModal activated={holidaymodalactivated} toggle={setHolidayModalActivated}></HolidayModal>
            {/* <ShortcutModal activated={shotcutmodalactivated} toggle={setShotcutmodalactivated}></ShortcutModal> */}
          </Frame>
          <CommandPalette />
        </Sentry.ErrorBoundary>
      </>
    </AppProvider>
  );
}

export default PageWrapper;
